import { usePrevious } from "@mantine/hooks";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { AssertValidParams, ParamsConstraint } from "./filter-params";
import type { FilterParamsHandle } from "./use-filter-params";
import { usePersistedFilterParams } from "./use-persisted-filter-params";

interface UseUrlSyncedFilterParamsResult<T extends ParamsConstraint> {
  handle: FilterParamsHandle<T>;
  initialized: boolean;
}

const PAGE_KEY = "page";

/**
 * Extends usePersistedFilterParams to sync the page parameter to the URL.
 *
 * This enables pagination state to survive page refreshes by storing the
 * current page in the URL query string (e.g., ?page=3).
 *
 * - On mount: reads page from URL if present, otherwise uses default
 * - On page change: updates URL using shallow routing (no page reload)
 * - Other filters: stored in localStorage (unchanged behavior)
 */
export const useUrlSyncedFilterParams = <T extends ParamsConstraint>(
  storageKeyPrefix: string,
  defaults: Partial<T & AssertValidParams<T>>,
): UseUrlSyncedFilterParamsResult<T> => {
  const pageKey = PAGE_KEY as keyof T;

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Track if we've initialized from URL (use state to trigger re-render)
  const [urlInitialized, setUrlInitialized] = useState(false);
  // Ref to prevent double-initialization
  const initializingRef = useRef(false);

  // Get persisted filter params (handles localStorage)
  const { handle: baseHandle, initialized: localStorageInitialized } =
    usePersistedFilterParams<T>(storageKeyPrefix, defaults);

  // Track previous localStorage init state to detect logout
  const prevLocalStorageInitialized = usePrevious(localStorageInitialized);

  // Reset URL state when localStorage resets (e.g., on logout)
  useEffect(() => {
    if (prevLocalStorageInitialized && !localStorageInitialized) {
      setUrlInitialized(false);
      initializingRef.current = false;
    }
  }, [localStorageInitialized, prevLocalStorageInitialized]);

  // Initialize page from URL on first render (after localStorage init)
  useEffect(() => {
    if (!localStorageInitialized || urlInitialized || initializingRef.current) {
      return;
    }

    initializingRef.current = true;

    const urlPage = searchParams.get(PAGE_KEY);
    if (urlPage) {
      const pageNum = parseInt(urlPage, 10);
      if (!isNaN(pageNum) && pageNum >= 1) {
        baseHandle.set(pageKey, pageNum as T[keyof T]);
      }
    }

    setUrlInitialized(true);
  }, [
    localStorageInitialized,
    urlInitialized,
    searchParams,
    baseHandle,
    pageKey,
  ]);

  // Sync state FROM URL when browser back/forward navigation occurs
  useEffect(() => {
    if (!urlInitialized) {
      return;
    }

    const urlPage = searchParams.get(PAGE_KEY);
    const urlPageNum = urlPage ? parseInt(urlPage, 10) : 1;
    const currentPage = baseHandle.getWithDefault(pageKey, 1 as T[keyof T]);

    // Only update if URL page is valid and differs from current state (avoids loops)
    if (!isNaN(urlPageNum) && urlPageNum >= 1 && urlPageNum !== currentPage) {
      baseHandle.set(pageKey, urlPageNum as T[keyof T]);
    }
  }, [searchParams, urlInitialized, baseHandle, pageKey]);

  // Update URL when page changes
  const updateUrlWithPage = useCallback(
    (page: number) => {
      const params = new URLSearchParams(searchParams.toString());

      if (page === 1) {
        // Remove page param if it's 1 (default)
        params.delete(PAGE_KEY);
      } else {
        params.set(PAGE_KEY, String(page));
      }

      const queryString = params.toString();
      const newUrl = queryString ? `${pathname}?${queryString}` : pathname;

      // Use push to add browser history entries so back button navigates between pages
      router.push(newUrl, { scroll: false });
    },
    [pathname, router, searchParams],
  );

  // Wrap handle to intercept page changes and update URL
  const handle: FilterParamsHandle<T> = useMemo(() => {
    // Helper to update URL when filter changes reset page to 1
    const updateUrlOnFilterChange = (key: keyof T) => {
      if (key !== pageKey && urlInitialized) {
        // usePersistedFilterParams resets page to 1 when filters change
        updateUrlWithPage(1);
      }
    };

    return {
      ...baseHandle,
      set: (key, value) => {
        baseHandle.set(key, value);

        // If page is being set directly, update URL with that value
        // If other filter is being set, usePersistedFilterParams resets page to 1
        if (urlInitialized) {
          if (key === pageKey) {
            updateUrlWithPage(value as number);
          } else {
            updateUrlWithPage(1);
          }
        }

        return handle;
      },
      append: (key, value) => {
        baseHandle.append(key, value);
        updateUrlOnFilterChange(key);
        return handle;
      },
      delete: ((key: keyof T, value?: unknown) => {
        if (value === undefined) {
          baseHandle.delete(key);
        } else {
          (baseHandle.delete as (k: keyof T, v: unknown) => void)(key, value);
        }
        updateUrlOnFilterChange(key);
        return handle;
      }) as FilterParamsHandle<T>["delete"],
      toggleValue: (key, value) => {
        baseHandle.toggleValue(key, value);
        updateUrlOnFilterChange(key);
        return handle;
      },
      clear: () => {
        baseHandle.clear();

        // When clearing, page resets to 1, so update URL
        if (urlInitialized) {
          updateUrlWithPage(1);
        }

        return handle;
      },
    };
  }, [baseHandle, pageKey, updateUrlWithPage, urlInitialized]);

  // Combined initialization: localStorage + URL
  const initialized = localStorageInitialized && urlInitialized;

  return { handle, initialized };
};
